import React, { useMemo, useState } from "react";
import { formatLettresDate } from "../../../utils/ClasseEvaluations";
import { Calendar, Users, BookOpen, CheckCircle, Clock, ChevronDown, ChevronUp, Trophy } from "lucide-react";

// Configuration des types d'activités
const ACTIVITY_CONFIGS = {
  AUTOMATISME: {
    icon: "⚡",
    bg: "bg-emerald-100",
    border: "border-emerald-400",
    text: "text-emerald-800",
    label: "Auto"
  },
  RESOLUTION: {
    icon: "🧩",
    bg: "bg-rose-100",
    border: "border-rose-400",
    text: "text-rose-800",
    label: "RdP"
  },
  QFLASH: {
    icon: "💡",
    bg: "bg-amber-100",
    border: "border-amber-400",
    text: "text-amber-800",
    label: "QFlash"
  },
};

// Badge compteur pour les activités
const ActivityBadge = ({ type, count }) => {
  const config = ACTIVITY_CONFIGS[type] || ACTIVITY_CONFIGS.AUTOMATISME;
  if (count === 0) return null;

  return (
    <div className={`
      flex items-center gap-1 px-2 py-1 rounded-lg
      ${config.bg} border ${config.border} ${config.text}
      text-xs font-bold
    `}>
      <span>{config.icon}</span>
      <span>{count}</span>
    </div>
  );
};

// Composant pour une carte de séquence avec état d'avancement
// mode: "classe" ou "groupe" - détermine si on utilise sequence.classes ou sequence.groupes
const SequenceProgressCard = ({ sequence, filteredItems, index, mode = "classe", activitesParalleles }) => {
  const [showItems, setShowItems] = useState(false);

  // Label dynamique selon le mode
  const itemLabel = mode === "groupe" ? "groupes" : "classes";

  // Calculer les items (classes ou groupes) qui ont fait cette séquence
  const itemsStatus = useMemo(() => {
    if (!filteredItems) return { done: [], notDone: [], total: 0, percentage: 0 };

    // Utiliser sequence.groupes ou sequence.classes selon le mode
    const sequenceItems = mode === "groupe" ? sequence.groupes : sequence.classes;

    const done = filteredItems.filter((item) =>
      sequenceItems?.includes(item.id)
    );
    const notDone = filteredItems.filter((item) =>
      !sequenceItems?.includes(item.id)
    );
    const total = filteredItems.length;
    const percentage = total > 0 ? Math.round((done.length / total) * 100) : 0;

    return { done, notDone, total, percentage };
  }, [sequence, filteredItems, mode]);

  const isComplete = itemsStatus.percentage === 100;
  const hasProgress = itemsStatus.done.length > 0;

  // Compter les activités par type
  const activityCounts = useMemo(() => {
    if (!activitesParalleles || activitesParalleles.length === 0) {
      return { AUTOMATISME: 0, QFLASH: 0, RESOLUTION: 0 };
    }

    const counts = { AUTOMATISME: 0, QFLASH: 0, RESOLUTION: 0 };
    activitesParalleles.forEach(act => {
      const type = act.type || 'AUTOMATISME';
      if (counts[type] !== undefined) {
        counts[type]++;
      }
    });
    return counts;
  }, [activitesParalleles]);

  const hasActivities = activityCounts.AUTOMATISME > 0 || activityCounts.QFLASH > 0 || activityCounts.RESOLUTION > 0;

  return (
    <div className={`
      relative group rounded-2xl overflow-hidden
      transition-all duration-300 hover:scale-[1.02]
      ${isComplete
        ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-300 shadow-lg shadow-emerald-100'
        : 'bg-white border-2 border-gray-200 shadow-md hover:shadow-xl'
      }
    `}>
      {/* Badge 100% complété */}
      {isComplete && (
        <div className="absolute -top-1 -right-1 z-10">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-3 py-1 rounded-bl-xl rounded-tr-xl text-xs font-bold flex items-center gap-1 shadow-lg">
            <Trophy className="w-3 h-3" />
            Complet
          </div>
        </div>
      )}

      {/* Header avec numéro - couleur uniforme bleu/sky */}
      <div className="px-4 py-3 bg-gradient-to-r from-sky-50 to-blue-50 border-b border-sky-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm text-white bg-gradient-to-br from-sky-500 to-blue-600 shadow-md">
              S{index + 1}
            </div>
            <span className="text-xs font-semibold uppercase tracking-wider text-sky-600">
              {sequence.theme_for_sequence}
            </span>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="p-4">
        {/* Titre */}
        <h3 className="font-bold text-gray-800 text-base mb-3 line-clamp-2">
          {sequence.title}
        </h3>

        {/* Dates */}
        <div className="flex items-center gap-2 mb-3 text-gray-500">
          <Calendar className="w-4 h-4" />
          <span className="text-xs">
            {formatLettresDate(sequence.start)} - {formatLettresDate(sequence.end)}
          </span>
        </div>

        {/* Badges activités parallèles */}
        {hasActivities && (
          <div className="flex flex-wrap items-center gap-1.5 mb-3">
            <ActivityBadge type="AUTOMATISME" count={activityCounts.AUTOMATISME} />
            <ActivityBadge type="QFLASH" count={activityCounts.QFLASH} />
            <ActivityBadge type="RESOLUTION" count={activityCounts.RESOLUTION} />
          </div>
        )}

        {/* Barre de progression */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-700">
                {itemsStatus.done.length}/{itemsStatus.total} {itemLabel}
              </span>
            </div>
            <span className={`text-sm font-bold ${
              isComplete ? 'text-emerald-600' : hasProgress ? 'text-orange-500' : 'text-gray-400'
            }`}>
              {itemsStatus.percentage}%
            </span>
          </div>
          <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${
                isComplete
                  ? 'bg-gradient-to-r from-emerald-400 to-teal-500'
                  : hasProgress
                    ? 'bg-gradient-to-r from-orange-400 to-amber-500'
                    : 'bg-gray-300'
              }`}
              style={{ width: `${itemsStatus.percentage}%` }}
            />
          </div>
        </div>

        {/* Toggle pour voir les items */}
        <button
          onClick={() => setShowItems(!showItems)}
          className="w-full flex items-center justify-between px-3 py-2 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors text-sm"
        >
          <span className="text-gray-600 font-medium">
            Voir les {itemLabel}
          </span>
          {showItems ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </button>

        {/* Liste des items (collapsible) */}
        {showItems && (
          <div className="mt-3 space-y-2 animate-fadeIn">
            {/* Items qui ont fait la séquence */}
            {itemsStatus.done.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-emerald-600 mb-1 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Séquence vue ({itemsStatus.done.length})
                </p>
                <div className="flex flex-wrap gap-1">
                  {itemsStatus.done.map((item) => (
                    <span
                      key={item.id}
                      className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium"
                    >
                      {item.nom}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Items qui n'ont pas fait la séquence */}
            {itemsStatus.notDone.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  En attente ({itemsStatus.notDone.length})
                </p>
                <div className="flex flex-wrap gap-1">
                  {itemsStatus.notDone.map((item) => (
                    <span
                      key={item.id}
                      className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium"
                    >
                      {item.nom}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// mode: "classe" ou "groupe" - détermine le comportement global
const SequencesCommunes = ({ sequenceProf, filteredClasses, filteredGroupes, niveau, mode = "classe" }) => {
  // Utiliser filteredClasses ou filteredGroupes selon le mode
  const filteredItems = mode === "groupe" ? filteredGroupes : filteredClasses;
  const itemLabel = mode === "groupe" ? "groupes" : "classes";

  // Trier les séquences (type SEQUENCE uniquement) par date de début
  const sortedSequences = useMemo(() => {
    if (!sequenceProf) return [];
    return [...sequenceProf]
      .filter(seq => seq.type === 'SEQUENCE')
      .sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [sequenceProf]);

  // Récupérer les activités parallèles pour chaque séquence
  const activitesParallelesMap = useMemo(() => {
    if (!sequenceProf || !sortedSequences) return {};

    const activites = {};

    // Filtrer les activités (non-SEQUENCE)
    const activities = sequenceProf.filter(seq => seq.type !== 'SEQUENCE');

    sortedSequences.forEach(seq => {
      activites[seq.id] = [];

      activities.forEach(act => {
        // Vérifier si l'activité tombe dans les dates de la séquence
        const actStart = new Date(act.start);
        const seqStart = new Date(seq.start);
        const seqEnd = new Date(seq.end);

        if (actStart >= seqStart && actStart <= seqEnd) {
          activites[seq.id].push(act);
        }
      });
    });

    return activites;
  }, [sequenceProf, sortedSequences]);

  // Stats globales
  const globalStats = useMemo(() => {
    if (!sortedSequences || !filteredItems) return { complete: 0, inProgress: 0, notStarted: 0 };

    let complete = 0;
    let inProgress = 0;
    let notStarted = 0;

    sortedSequences.forEach((seq) => {
      // Utiliser seq.groupes ou seq.classes selon le mode
      const seqItems = mode === "groupe" ? seq.groupes : seq.classes;
      const doneCount = filteredItems.filter((item) => seqItems?.includes(item.id)).length;

      if (doneCount === filteredItems.length) {
        complete++;
      } else if (doneCount > 0) {
        inProgress++;
      } else {
        notStarted++;
      }
    });

    return { complete, inProgress, notStarted };
  }, [sortedSequences, filteredItems, mode]);

  return (
    <div className=" overflow-hidden">
      {/* Header - Style adapté pour fond clair */}
      <div className="bg-white/80 backdrop-blur-md rounded-xl px-4 sm:px-6 py-4 border border-sky-200 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-blue-500 to-sky-500 rounded-xl shadow-lg">
              <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-800">
               Séquences communes
              </h2>
              <p className="text-gray-600 text-xs sm:text-sm">
                Niveau {niveau} - {filteredItems?.length || 0} {itemLabel}
              </p>
            </div>
          </div>

          {/* Stats rapides */}
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 border border-emerald-300 rounded-lg">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <span className="text-emerald-700 font-bold text-sm">{globalStats.complete}</span>
              <span className="text-emerald-600 text-xs hidden sm:inline">communes</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-100 border border-orange-300 rounded-lg">
              <Clock className="w-4 h-4 text-orange-600" />
              <span className="text-orange-700 font-bold text-sm">{globalStats.inProgress}</span>
              <span className="text-orange-600 text-xs hidden sm:inline">en cours</span>
            </div>
          </div>
        </div>
      </div>

      {/* Grille des séquences */}
      <div className="p-4 sm:p-6">
        {sortedSequences.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedSequences.map((seq, index) => (
              <SequenceProgressCard
                key={seq.id}
                sequence={seq}
                filteredItems={filteredItems}
                index={index}
                mode={mode}
                activitesParalleles={activitesParallelesMap[seq.id] || []}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="p-4 bg-gray-100 rounded-full mb-4">
              <BookOpen className="w-10 h-10 text-gray-400" />
            </div>
            <p className="text-gray-500 text-center max-w-md">
              Aucune séquence disponible pour le niveau <span className="font-semibold">{niveau}</span>.
            </p>
          </div>
        )}
      </div>

      {/* Animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default SequencesCommunes;
